import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

const reducer = (state, action) => {
  if (action.type === "LOAD_TAGS") {
    const tags = action.payload;
    const newTags = [];
    tags.forEach((tag) => {
      const tagIndex = state.findIndex((s) => s.id === tag.id);
      if (tagIndex !== -1) {
        state[tagIndex] = tag;
      } else {
        newTags.push(tag);
      }
    });
    return [...state, ...newTags];
  }
  if (action.type === "UPDATE_TAGS") {
    const tag = action.payload;
    const tagIndex = state.findIndex((s) => s.id === tag.id);
    if (tagIndex !== -1) {
      state[tagIndex] = tag;
      return [...state];
    } else {
      return [tag, ...state];
    }
  }
  if (action.type === "DELETE_TAGS") {
    const tagId = action.payload;
    const tagIndex = state.findIndex((s) => s.id === tagId);
    if (tagIndex !== -1) {
      state.splice(tagIndex, 1);
    }
    return [...state];
  }
  if (action.type === "RESET") {
    return [];
  }
};

const styles = {
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  headerActions: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  button: {
    padding: "10px 20px",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  buttonPrimary: {
    background: "var(--primaryColor, #065183)",
    color: "white",
  },
  buttonPrimaryHover: {
    background: "#2563eb",
    transform: "translateY(-1px)",
    boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
  },
  buttonSecondary: {
    background: "#e8ecf1",
    color: "#1a1a1a",
  },
  buttonSecondaryHover: {
    background: "#d1d5db",
  },
  card: {
    background: "white",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    overflow: "hidden",
  },
  tableContainer: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    background: "#f9fafb",
    borderBottom: "2px solid #e8ecf1",
    position: "sticky",
    top: 0,
  },
  tableHeaderCell: {
    padding: "12px 16px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    whiteSpace: "nowrap",
  },
  tableRow: {
    borderBottom: "1px solid #e8ecf1",
    transition: "background 0.2s ease",
  },
  tableRowHover: {
    background: "#f9fafb",
  },
  tableCell: {
    padding: "12px 16px",
    fontSize: "13px",
    color: "#1a1a1a",
  },
  chip: {
    display: "inline-block",
    padding: "6px 12px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 600,
    color: "white",
    textShadow: "0 1px 1px rgba(0, 0, 0, 0.2)",
  },
  actionButtons: {
    display: "flex",
    gap: "6px",
  },
  actionButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    transition: "all 0.2s ease",
    color: "#6c757d",
  },
  actionButtonHover: {
    background: "#f0f0f0",
    color: "#1a1a1a",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#999",
  },
  loadingState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#666",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "12px",
    marginTop: "20px",
    padding: "16px",
  },
  paginationButton: {
    padding: "6px 12px",
    border: "1px solid #e8ecf1",
    background: "white",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: 600,
    color: "#1a1a1a",
    transition: "all 0.3s ease",
  },
};

const Tags = () => {
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTags = async () => {
        try {
          const { data } = await api.get("/tags/", {
            params: { searchParam, pageNumber, kanban: 1, limit: pageSize },
          });
          dispatch({ type: "LOAD_TAGS", payload: data.tags });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchTags();
    }, 200); // Reduzir delay de 500ms para 200ms
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, pageSize]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const onTagsEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAGS", payload: +data.tagId });
      }
    };
    socket.on(`company${user.companyId}-tag`, onTagsEvent);
    return () => {
      socket.off(`company${user.companyId}-tag`, onTagsEvent);
    };
  }, [socket, user.companyId]);

  const handleOpenTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleReturnToKanban = () => {
    history.push("/kanban");
  };

  return (
    <div style={styles.container}>
      <ConfirmationModal
        title={
          deletingTag && `${i18n.t("tagsKanban.confirmationModal.deleteTitle")}`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tagsKanban.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      {tagModalOpen && (
        <TagModal
          open={tagModalOpen}
          onClose={handleCloseTagModal}
          aria-labelledby="form-dialog-title"
          tagId={selectedTag && selectedTag.id}
          kanban={1}
        />
      )}

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🏷️ {i18n.t("tagsKanban.title")}</h1>
        <div style={styles.headerActions}>
          <button
            style={{
              ...styles.button,
              ...styles.buttonSecondary,
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#d1d5db";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#e8ecf1";
            }}
            onClick={handleReturnToKanban}
          >
            <ArrowBackIcon style={{ fontSize: "16px" }} /> {i18n.t("tagsKanban.backToKanban")}
          </button>
          <button
            style={{
              ...styles.button,
              ...styles.buttonPrimary,
              padding: "10px",
              minWidth: "auto",
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "var(--primaryColor, #065183)";
              e.target.style.opacity = "0.8";
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.2)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "var(--primaryColor, #065183)";
              e.target.style.opacity = "1";
              e.target.style.transform = "none";
              e.target.style.boxShadow = "none";
            }}
            onClick={handleOpenTagModal}
            title={i18n.t("tagsKanban.newStage")}
          >
            <AddIcon style={{ fontSize: "20px" }} />
          </button>
        </div>
      </div>

      {/* Search */}
      <div style={{ ...styles.card, marginBottom: "24px", padding: "16px" }}>
        <input
          type="text"
          placeholder={i18n.t("tagsKanban.searchPlaceholder")}
          value={searchParam}
          onChange={handleSearch}
          style={{
            width: "100%",
            padding: "10px 12px",
            border: "1px solid #e8ecf1",
            borderRadius: "6px",
            fontSize: "13px",
            fontFamily: "inherit",
            background: "#fafbfc",
            transition: "all 0.2s ease",
            boxSizing: "border-box",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "#3b82f6";
            e.target.style.background = "white";
            e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "#e8ecf1";
            e.target.style.background = "#fafbfc";
            e.target.style.boxShadow = "none";
          }}
        />
      </div>

      {/* Table */}
      <div style={styles.card} onScroll={handleScroll}>
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead style={styles.tableHeader}>
              <tr>
                <th style={styles.tableHeaderCell}>{i18n.t("tagsKanban.stage")}</th>
                <th style={styles.tableHeaderCell}>{i18n.t("tagsKanban.tickets")}</th>
                <th style={styles.tableHeaderCell}>{i18n.t("tagsKanban.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {tags.map((tag) => (
                <tr
                  key={tag.id}
                  style={styles.tableRow}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <td style={styles.tableCell}>
                    <div
                      style={{
                        ...styles.chip,
                        backgroundColor: tag.color,
                      }}
                    >
                      {tag.name}
                    </div>
                  </td>
                  <td style={styles.tableCell}>
                    <span style={{ fontWeight: 600, color: "#3b82f6" }}>
                      {tag?.ticketTags ? tag?.ticketTags?.length : 0}
                    </span>
                  </td>
                  <td style={styles.tableCell}>
                    <div style={styles.actionButtons}>
                      <button
                        style={styles.actionButton}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f0f0f0";
                          e.currentTarget.style.color = "#1a1a1a";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "none";
                          e.currentTarget.style.color = "#6c757d";
                        }}
                        onClick={() => handleEditTag(tag)}
                        title={i18n.t("tagsKanban.edit")}
                      >
                        <EditIcon style={{ fontSize: "16px" }} />
                      </button>
                      <button
                        style={styles.actionButton}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#fee2e2";
                          e.currentTarget.style.color = "#dc2626";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "none";
                          e.currentTarget.style.color = "#6c757d";
                        }}
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingTag(tag);
                        }}
                        title={i18n.t("tagsKanban.delete")}
                      >
                        <DeleteOutlineIcon style={{ fontSize: "16px" }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && tags.length === 0 && (
          <div style={styles.loadingState}>
            <p>{i18n.t("tagsKanban.loading")}</p>
          </div>
        )}

        {!loading && tags.length === 0 && (
          <div style={styles.emptyState}>
            <p>{i18n.t("tagsKanban.noStagesFound")}</p>
          </div>
        )}

        {hasMore && (
          <div style={styles.pagination}>
            <button
              style={{
                ...styles.paginationButton,
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#f9fafb";
                e.target.style.borderColor = "#3b82f6";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "white";
                e.target.style.borderColor = "#e8ecf1";
              }}
              onClick={loadMore}
              disabled={loading}
            >
              {loading ? i18n.t("tagsKanban.loadingMore") : i18n.t("tagsKanban.loadMore")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tags;
